import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Divider,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InfoIcon from '@mui/icons-material/Info';
import { saveRegressionResultsAsync } from '../../redux/regressionResults/saveRegressionResultsSlice';
import { selectValidationMetadata } from '../../redux/validation/validationResultsSlice';
import { selectRegressionTestId, selectUserEmail } from '../../redux/app/configSlice';
import {
  getRegressionTestIdsAsync,
  selectRegressionTestIds,
  selectRegressionTestIdsLoading,
} from '../../redux/regressionTestIds/getRegressionTestIdsSlice';

export default function SaveResultsDialog({ open, onClose, testData }) {
  const dispatch = useDispatch();
  const [regressionTestId, setRegressionTestId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Get metadata from Redux
  const metadata = useSelector(selectValidationMetadata);

  // Get stored regression test ID from Redux
  const storedRegressionTestId = useSelector(selectRegressionTestId);

  // Get user email from Redux (set from Stytch on login)
  const userEmail = useSelector(selectUserEmail);

  // Get regression test IDs for autocomplete
  const regressionTestIds = useSelector(selectRegressionTestIds);
  const loadingIds = useSelector(selectRegressionTestIdsLoading);

  const noRegressionIds = !regressionTestIds || regressionTestIds.length === 0;

  // Auto-populate regression test ID and fetch IDs when dialog opens
  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem('regressionTestId') || storedRegressionTestId || '';
      setRegressionTestId(stored);
      dispatch(getRegressionTestIdsAsync());
    }
  }, [open, storedRegressionTestId, dispatch]);

  // Format metadata for display with fallbacks
  const displayMetadata = useMemo(() => {
    if (!metadata) {
      return {
        flow: 'N/A',
        testCaseId: 'N/A',
        marketName: 'N/A',
        deviceName: 'N/A',
        deviceId: 'N/A',
        deviceType: 'N/A',
        startTime: 'N/A',
        endTime: 'N/A',
        testerEmail: 'N/A',
        executedAt: new Date().toLocaleString(),
      };
    }

    // Format Unix timestamps to readable strings for display
    const formatTimestamp = (unixMs) => {
      if (!unixMs || unixMs === 'N/A') return 'N/A';
      return new Date(unixMs).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    };

    return {
      flow: metadata.flow_name || 'N/A',
      testCaseId: metadata.test_case_id || 'N/A',
      marketName: metadata.market_name || 'N/A',
      deviceName: metadata.device_name || 'N/A',
      deviceId: metadata.device_hardware_id || 'N/A', // Display hardware ID (IDFA/IDFV)
      deviceType: metadata.device_type || 'N/A',
      startTime: formatTimestamp(metadata.start_time),
      endTime: formatTimestamp(metadata.end_time),
      testerEmail: metadata.tester_email || userEmail,
      executedAt: new Date().toLocaleString(),
    };
  }, [metadata, userEmail]);

  // Helper function to check if flowComparison has any attribute failures
  const hasAttributeFailures = (flowComparison) => {
    return (
      flowComparison &&
      Array.isArray(flowComparison) &&
      flowComparison.some((attr) => attr.passFail === 'Fail' || attr.passFail === 'Missing')
    );
  };

  // Compute summary from testData
  const summary = useMemo(() => {
    if (!testData || testData.length === 0) {
      return {
        total_events: 0,
        passed: 0,
        failed: 0,
        missing: 0,
        order_issues: 0,
        pass_rate: '0.00',
        non_validated: 0,
        not_present_in_app: 0,
      };
    }

    // Separate validated from non-validated events
    // Validated = events with flowComparison OR isMissing (part of the flow)
    // Non-validated = events without flowComparison and not missing (extra events)
    const validatedEvents = testData.filter((b) => b.flowComparison || b.isMissing);
    const nonValidatedEvents = testData.filter((b) => !b.flowComparison && !b.isMissing);

    const total_events = validatedEvents.length;
    const non_validated = nonValidatedEvents.length;
    const missing = testData.filter((b) => b.isMissing).length;
    const order_issues = testData.filter((b) => b.hasOrderIssue).length;

    // Count passed/failed/missing respective manual status overrides
    const not_present_in_app = validatedEvents.filter(
      (b) => b.manualBeaconStatus === 'NOT_PRESENT_IN_APP',
    ).length;

    const passed = validatedEvents.filter((b) => {
      // manual override
      if (b.manualBeaconStatus === 'Pass') {
        return true;
      }

      if (b.manualBeaconStatus === 'Fail' || b.manualBeaconStatus === 'NOT_PRESENT_IN_APP') {
        return false;
      }

      return (
        b.flowComparison &&
        !b.hasOrderIssue &&
        !b.isMissing &&
        !hasAttributeFailures(b.flowComparison)
      );
    }).length;

    const failed = validatedEvents.filter((b) => {
      // manual override
      if (b.manualBeaconStatus === 'Fail') {
        return true;
      }

      if (b.manualBeaconStatus === 'Pass' || b.manualBeaconStatus === 'NOT_PRESENT_IN_APP') {
        return false;
      }

      return (
        b.flowComparison &&
        (b.hasOrderIssue || b.isMissing || hasAttributeFailures(b.flowComparison))
      );
    }).length;

    // Calculate pass rate from validated events only
    const validationTotal = total_events - not_present_in_app;

    const pass_rate = validationTotal > 0 ? ((passed / validationTotal) * 100).toFixed(2) : '0.00';

    return {
      total_events,
      passed,
      failed,
      missing,
      order_issues,
      pass_rate,
      non_validated,
      not_present_in_app,
    };
  }, [testData]);

  const renderMetaItem = (label, value) => (
    <Grid item xs={6} sm={4}>
      <Typography variant="subtitle2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Grid>
  );
  const buildValidationStatePayload = (data) => {
    return data.map((item) => ({
      beaconID: item.beaconID,
      index: item.index,
      timestamp: item.timestamp,
      beaconType: item.beaconType,

      eventName: item.eventName,
      stepName: item.stepName,
      stepNumber: item.stepNumber,

      manualBeaconStatus: item.manualBeaconStatus || '',

      modifiedRows: item.modifiedRows || {},

      notes: item.notes || '',

      isMissing: item.isMissing || false,
      hasOrderIssue: item.hasOrderIssue || false,

      actualSequence: item.actualSequence,

      flowComparison: item.flowComparison || [],

      truth: item.truth || {},

      beaconData: item.beaconData || {},
    }));
  };

  const handleSubmit = async () => {
    if (!regressionTestId) {
      alert('Please select a valid Regression Test ID');
      return;
    }

    if (!metadata) {
      return alert('No validation metadata found. Please run a validation first.');
    }

    setLoading(true);
    setSaved(false);

    const payload = {
      regression_test_id: regressionTestId,
      notes,
      flow_db_id: metadata.flow_db_id, // integer - API maps to test_flow_id internally
      device_id: String(metadata.device_id), // string representation of database ID
      market_id: metadata.market_id, // integer - database ID
      executed_at: Date.now(), // Unix milliseconds
      start_time: metadata.start_time, // Unix milliseconds
      end_time: metadata.end_time, // Unix milliseconds
      tester_email: metadata.tester_email,
      summary,
      validation_state: buildValidationStatePayload(testData), // Full Redux state for validation results
    };

    try {
      // Dispatch the save regression results thunk (two-step S3 upload)
      const result = await dispatch(saveRegressionResultsAsync(payload));

      if (result.success) {
        console.log('Regression results saved successfully:', result.fileName);
        setSaved(true);
        onClose(); // Close modal after successful save
      } else {
        throw new Error(result.error || 'Failed to save test results');
      }
    } catch (err) {
      console.error('Error saving test results:', err);
      alert(`Failed to save test results: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRegressionTestId('');
    setNotes('');
    setSaved(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon />
            Save Test Results
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={2}>
            <Autocomplete
              fullWidth
              options={regressionTestIds || []}
              value={regressionTestId || null}
              disabled={noRegressionIds}
              onChange={(event, newValue) => setRegressionTestId(newValue || '')}
              loading={loadingIds}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Regression Test ID"
                  required
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingIds ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText={
                    noRegressionIds ? 'No Regression IDs exist. Please create one in Settings.' : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'text.secondary',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'primary.main',
                    },
                  }}
                />
              )}
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              margin="normal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Test Metadata</Typography>
          </Box>
          <Box mb={2}>
            <Grid container spacing={2}>
              {renderMetaItem('Flow', displayMetadata.flow)}
              {renderMetaItem('Test Case ID', displayMetadata.testCaseId)}
              {renderMetaItem('Market Name', displayMetadata.marketName)}
              {renderMetaItem('Executed At', displayMetadata.executedAt)}
              {renderMetaItem('Start Time', displayMetadata.startTime)}
              {renderMetaItem('End Time', displayMetadata.endTime)}
              {renderMetaItem('Tester Email', displayMetadata.testerEmail)}
              {renderMetaItem('Device Name', displayMetadata.deviceName)}
              {renderMetaItem('Device ID', displayMetadata.deviceId)}
              {renderMetaItem('Device Type', displayMetadata.deviceType)}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AssessmentIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Validation Summary</Typography>
          </Box>
          <Box mb={2}>
            <Grid container spacing={2}>
              {renderMetaItem('Total Events', summary.total_events)}
              {renderMetaItem('Passed', summary.passed)}
              {renderMetaItem('Failed', summary.failed)}
              {renderMetaItem('Not Present In App', summary.not_present_in_app)}
              {renderMetaItem('Missing', summary.missing)}
              {renderMetaItem('Order Issues', summary.order_issues)}
              {renderMetaItem('Pass Rate', `${summary.pass_rate}%`)}
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: 'custom.surfaceAlt',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle2" color="textSecondary">
                    Non-Validated Events (extra events not in test case)
                  </Typography>
                  <Typography variant="body1" color="textSecondary">
                    {summary.non_validated}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} disabled={loading} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1, color: 'primary.contrastText' }} />
                Saving...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSaved(false)}>
          Results saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
}
